import CmsDataProvider, { CMS_DATA_ID } from "@/providers/cms.provider";
import repository, { SportactiviteitKosten } from "@/repositories/bestel.repository";
import activiteitRepository, { ActiviteitItem, ActiviteitLesWithTrainers, BekostigingsType, SportmomentVraag, VraagBedoeldVoor } from "@/repositories/activiteit.repository";
import sportaanbiederRepository, { ISportaanbieder, ISportaanbiederLocatie } from "@/repositories/sportaanbieder.repository";
import profileRepository, { AccountGegevens, GolfstartAllowed } from "@/repositories/profile.repository";
import actiecodeRepository, { Actiecode, Spaarpunt } from "@/repositories/actiecode.repository";
import { usePlatformStore } from "@/stores/platform.store";
import { useProfileStore } from "@/stores/profile.store";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import locatieRepository from "@/repositories/locatie.repository";

export type BoekingGroep = {
    Begeleiders: number;
    Deelnemers: number;
    OrganisatieNaam: string;
    GroepMaxLeeftijd: number;
    GroepMinLeeftijd: number;
};

export type VraagAntwoord = {
    Id: number;
    Vraag: string;
    VraagType: number;
    Antwoord: string;
};

export type VolwasseneItem = {
    Voornaam: string;
    Achternaam: string;
    Leeftijd: number | null;
    Geslacht: string;
    Emailadres: string;
    Vragen: VraagAntwoord[];
};

export type KindItem = {
    Id?: number;
    Voornaam: string;
    Achternaam: string;
    Leeftijd: number | null;
    Vragen: VraagAntwoord[];
};

type Boeking = {
    Jezelf: {
        Include: boolean;
        Vragen: VraagAntwoord[];
    };
    Volwassenen: VolwasseneItem[];
    Kinderen: KindItem[];
    Vragen: VraagAntwoord[];
    Begeleiders: number;
    Deelnemers: number;
    Groepen: BoekingGroep[];
    PaymentProvider: number | null;
    Bijzonderheden: string;
    ExtraKostenAkkoord: boolean;
    AkkoordVoorwaarden: boolean;
};

type SidebarItem = {
    icon: string | null;
    text: string;
};

type SportactiviteitBestelCmsData = {
    selectedPaymentMethods: string[];
    activiteitId?: string;
    wieGaanErContentblockTitle?: string;
    sidebarItems?: SidebarItem[];
    terugUrl?: string;
    algemeneVoorwaardenText?: string;
    algemeneVoorwaardenLabel?: string;
};

export const useSportactiviteitBestelStore = defineStore("sportactiviteit-bestel", () => {
    const cmsData = CmsDataProvider.get<SportactiviteitBestelCmsData>(CMS_DATA_ID.SPORTACTIVITEIT_BESTEL);
    const platformStore = usePlatformStore();
    const profileStore = useProfileStore();

    const selectedPaymentMethods = ref<string[]>(cmsData.selectedPaymentMethods || []);
    const activiteitId = ref<string>(cmsData.activiteitId || "");
    const wieGaanErContentblockTitle = ref<string>(cmsData.wieGaanErContentblockTitle || "");
    const sidebarItems = ref<SidebarItem[]>(cmsData.sidebarItems || []);
    const terugUrl = ref<string>(cmsData.terugUrl || "/");
    const algemeneVoorwaardenText = ref<string>(cmsData.algemeneVoorwaardenText || "");
    const algemeneVoorwaardenLabel = ref<string>(cmsData.algemeneVoorwaardenLabel || "");
    const isInitialized = ref(false);
    const isLoading = ref(false);
    const isLoadingKosten = ref(false);
    const vragen = ref<SportmomentVraag[]>([]);
    const activiteit = ref<ActiviteitItem | null>(null);
    const lessen = ref<ActiviteitLesWithTrainers[] | null>(null);
    const locatie = ref<ISportaanbiederLocatie | null>(null);
    const aanbieder = ref<ISportaanbieder | null>(null);
    const alleKosten = ref<SportactiviteitKosten | null>(null);
    const initError = ref<string>("");
    const paymentMethods = ref<import("@/repositories/bestel.repository").PaymentMethod[]>([]);
    const boeking = ref<Boeking>({
        Jezelf: {
            Include: false,
            Vragen: [],
        },
        Volwassenen: [],
        Kinderen: [],
        Vragen: [],
        Begeleiders: 0,
        Deelnemers: 0,
        Groepen: [],
        PaymentProvider: null,
        Bijzonderheden: "",
        ExtraKostenAkkoord: false,
        AkkoordVoorwaarden: false,
    });

    const platformId = computed(() => platformStore.platformId || 0);
    const platform = computed(() => platformStore.platform || null);
    const hasPaymentMethods = computed(() => selectedPaymentMethods.value.length > 0);
    const aantalPersonen = computed(() => {
        const basis =
            boeking.value.Kinderen.length +
            boeking.value.Volwassenen.length +
            (boeking.value.Jezelf.Include ? 1 : 0) +
            (boeking.value.Deelnemers || 0) +
            (boeking.value.Begeleiders || 0);

        const groepenTotaal = boeking.value.Groepen.reduce((sum, group) => {
            return sum + (group.Deelnemers || 0) + (group.Begeleiders || 0);
        }, 0);

        return basis + groepenTotaal;
    });
    const aantalGroepen = computed(() => boeking.value.Groepen.length);

    const aantalPlaatsenBeschikbaar = computed(() => {
        if (!activiteit.value) return 0;
        if (activiteit.value.AantalPlaatsen <= activiteit.value.AantalInschrijvingen) return 0;
        if (activiteit.value.TotaalAantalPlaatsen !== activiteit.value.AantalPlaatsen) {
            const totaal = activiteit.value.TotaalAantalPlaatsen - activiteit.value.TotaalAantalInschrijvingen;
            const eigen = activiteit.value.AantalPlaatsen - activiteit.value.AantalInschrijvingen;
            if (totaal > activiteit.value.AantalPlaatsen) return eigen;
            if (totaal > eigen) return eigen;
            return totaal;
        }
        return activiteit.value.TotaalAantalPlaatsen - activiteit.value.TotaalAantalInschrijvingen;
    });

    const isGeweest = computed(() => {
        if (!activiteit.value?.EindDatum) return false;
        return new Date(activiteit.value.EindDatum) < new Date();
    });

    const isInschrijvingPeriodeActief = computed(() => {
        if (!activiteit.value) return true;
        const now = new Date();
        const vanafDatum = activiteit.value.InschrijvenVanafDatum;
        const totDatum = activiteit.value.InschrijvenTotDatum;
        if (vanafDatum && now < new Date(vanafDatum)) return false;
        if (totDatum && now > new Date(totDatum)) return false;
        return true;
    });

    const isAbonnement = computed(() =>
        activiteit.value?.Bekostigingstype === BekostigingsType.ABONNEMENT,
    );

    // TODO: expose the abonnement period on activiteit so this can be read from the API instead of a fixed value.
    const abonnementPeriode = computed(() => "maand");

    const individueleVragen = computed(() =>
        vragen.value.filter((v) => v.VraagBedoeldVoor === VraagBedoeldVoor.PerDeelnemer),
    );

    const groepsVragen = computed(() =>
        vragen.value.filter((v) => v.VraagBedoeldVoor === VraagBedoeldVoor.VoorDeGroep),
    );

    const buildVraagAntwoorden = (vraagLijst: SportmomentVraag[]): VraagAntwoord[] =>
        vraagLijst.map((v) => ({ Id: v.Id, Vraag: v.Vraag, VraagType: v.VraagType, Antwoord: "" }));

    const loadKosten = async () => {
        isLoadingKosten.value = true;
        if (!activiteitId.value || !platformId.value) {
            return;
        }

        alleKosten.value = await repository.getAllKosten(
            activiteitId.value,
            platformId.value,
            aantalPersonen.value,
            aantalGroepen.value,
            validActiecode.value?.Id,
        );

        isLoadingKosten.value = false;
    };

    const setJezelfInclude = (include: boolean) => {
        boeking.value.Jezelf.Include = include;
        if (include && boeking.value.Jezelf.Vragen.length === 0) {
            boeking.value.Jezelf.Vragen = buildVraagAntwoorden(individueleVragen.value);
        }
    };

    const addKind = () => {
        boeking.value.Kinderen.push({ Voornaam: "", Achternaam: "", Leeftijd: null, Vragen: buildVraagAntwoorden(individueleVragen.value) });
    };

    const removeKind = (index?: number) => {
        if (boeking.value.Kinderen.length > 0) {
            const idx = index ?? boeking.value.Kinderen.length - 1;
            boeking.value.Kinderen.splice(idx, 1);
        }
    };

    const addVolwassene = () => {
        boeking.value.Volwassenen.push({ Voornaam: "", Achternaam: "", Leeftijd: null, Geslacht: "", Emailadres: "", Vragen: buildVraagAntwoorden(individueleVragen.value) });
    };

    const removeVolwassene = (index?: number) => {
        if (boeking.value.Volwassenen.length > 0) {
            const idx = index ?? boeking.value.Volwassenen.length - 1;
            boeking.value.Volwassenen.splice(idx, 1);
        }
    };

    const setDeelnemers = (value: number) => {
        boeking.value.Deelnemers = Math.max(0, value);
    };

    const setBegeleiders = (value: number) => {
        boeking.value.Begeleiders = Math.max(0, value);
    };

    const addGroup = () => {
        const groupNumber = boeking.value.Groepen.length + 1;
        boeking.value.Groepen.push({ Begeleiders: 0, Deelnemers: 0, OrganisatieNaam: `Groep ${groupNumber}`, GroepMaxLeeftijd: 0, GroepMinLeeftijd: 0 });
    };

    const removeGroup = (index: number) => {
        if (boeking.value.Groepen.length > 1) {
            boeking.value.Groepen.splice(index, 1);
        }
    };

    const guestAccount = ref(false);
    const upgradeProfile = ref(false);
    const isSubmitting = ref(false);
    const submitError = ref("");
    const maximaalAantalKeerSporten = ref<unknown[]>([]);
    const hasRestoredBoeking = ref(false);

    // ── Golfstart (platform 7 + IsGolfstart2) ─────────────────────────────────
    const golfstartAllowed = ref<GolfstartAllowed[]>([]);
    const isProfileAllowed = ref(true);
    const isFriendsAllowed = ref(true);
    const isGolfstartAllowed = computed(() => isProfileAllowed.value && isFriendsAllowed.value);

    const checkGolfstart = async () => {
        if (platformId.value !== 7 || !activiteit.value?.IsGolfstart2) return;
        try {
            if (golfstartAllowed.value.length === 0) {
                golfstartAllowed.value = await profileRepository.getGolfstartAllowed();
            }
            if (!activiteit.value?.Sportaanbieder) return;
            const providerEntries = golfstartAllowed.value.filter(
                (e) => e.SportaanbiederId === activiteit.value!.Sportaanbieder,
            );
            const friendsList: string[] = [];
            providerEntries.forEach((e) => friendsList.push(...e.FriendsAsList));

            if (providerEntries.length > 0 && providerEntries[0].Golfstart2Allowed) {
                isProfileAllowed.value = providerEntries[0].Golfstart2Allowed;
            } else {
                isProfileAllowed.value = false;
                isFriendsAllowed.value = false;
            }

            if (boeking.value.Volwassenen.length > 0) {
                if (friendsList.length > 0) {
                    isFriendsAllowed.value = boeking.value.Volwassenen.every((v) =>
                        friendsList.includes(v.Emailadres),
                    );
                } else {
                    isFriendsAllowed.value = false;
                }
            } else {
                isFriendsAllowed.value = true;
            }
        } catch (err) {
            console.error(err);
        }
    };

    // ── Payment method visibility ──────────────────────────────────────────────
    const showPaymentMethod = computed(() => {
        if (isAbonnement.value) {
            return true;
        }
        return !validActiecode.value || (alleKosten.value?.TotaalKosten ?? 0) > 0;
    });

    // ── Credits (spaarpunten) ──────────────────────────────────────────────────
    const spaarpunten = ref<Spaarpunt[]>([]);
    const spaarpuntenCount = computed(() =>
        spaarpunten.value.filter((s) => s.Sportmoment === null).length,
    );
    const remainingSpaarpunten = computed(() => spaarpuntenCount.value - aantalPersonen.value);
    const notEnoughCredits = computed(
        () =>
            activiteit.value?.Bekostigingstype === 1 &&
            profileStore.isLoggedIn &&
            remainingSpaarpunten.value < 0,
    );

    const loadSpaarpunten = async () => {
        if (profileStore.isLoggedIn) {
            spaarpunten.value = await actiecodeRepository.getSpaarpunten();
        }
    };

    // ── Kortingscode (actiecode) ───────────────────────────────────────────────
    const showKortingscodeOptie = ref(false);
    const kortingscode = ref("");
    const validActiecode = ref<Actiecode | null>(null);
    const invalidActiecode = ref(false);
    const lockedOut = ref(false);
    const isValidatingActiecode = ref(false);

    const validateActiecode = async () => {
        if (lockedOut.value || !kortingscode.value) return;
        isValidatingActiecode.value = true;
        validActiecode.value = null;
        invalidActiecode.value = false;
        try {
            const result = await actiecodeRepository.validateActiecode(kortingscode.value, activiteitId.value);
            if (result) {
                validActiecode.value = result;
                await loadKosten();
            } else {
                invalidActiecode.value = true;
            }
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            if (status === 409) {
                lockedOut.value = true;
            } else {
                invalidActiecode.value = true;
            }
        } finally {
            isValidatingActiecode.value = false;
        }
    };

    const resetActiecode = () => {
        validActiecode.value = null;
        invalidActiecode.value = false;
        lockedOut.value = false;
        kortingscode.value = "";
        loadKosten();
    };

    const continueAsGuest = () => {
        if (isAbonnement.value) {
            return;
        }
        guestAccount.value = true;
    };

    const account = ref<AccountGegevens>({
        Voornaam: "",
        Achternaam: "",
        Emailadres: "",
        EmailadresConfirm: "",
        Geboortedatum: null,
        Postcode: "",
        Telefoonnummer: "",
        Noodnummer: "",
    });

    const mapVragenAntwoorden = (vragen: VraagAntwoord[]) =>
        vragen.map((v) => ({ SportmomentVraagId: v.Id, Vraag: v.Vraag, VraagType: v.VraagType, Antwoord: v.Antwoord }));

    type SubmitResult =
        | { type: "success" }
        | { type: "payment"; url: string }
        | { type: "error"; message: string };

    const submitBoeking = async (): Promise<SubmitResult> => {
        if (!activiteit.value) return { type: "error", message: "Activiteit niet gevonden." };

        isSubmitting.value = true;
        submitError.value = "";
        maximaalAantalKeerSporten.value = [];

        try {
            // Update profiel for logged-in users
            if (!guestAccount.value && profileStore.isLoggedIn) {
                await profileRepository.postProfiel(account.value);
            }

            const inschrijvingen: unknown[] = [];

            if (isAbonnement.value) {
                boeking.value.Jezelf.Include = true;
            }

            // Jezelf
            if (boeking.value.Jezelf.Include) {
                inschrijvingen.push({
                    Voornaam: account.value.Voornaam,
                    Achternaam: account.value.Achternaam,
                    VragenAntwoorden: mapVragenAntwoorden(boeking.value.Jezelf.Vragen),
                });
            }

            // Groepen (groepsactiviteit)
            if (activiteit.value?.Groepsactiviteit) {
                for (const groep of boeking.value.Groepen) {
                    inschrijvingen.push({
                        Voornaam: account.value.Voornaam,
                        Achternaam: account.value.Achternaam,
                        Deelnemers: groep.Deelnemers,
                        Begeleiders: groep.Begeleiders,
                        OrganisatieNaam: groep.OrganisatieNaam,
                        GroepMaxLeeftijd: groep.GroepMaxLeeftijd,
                        GroepMinLeeftijd: groep.GroepMinLeeftijd,
                        VragenAntwoorden: [],
                    });
                }
            }

            // Volwassenen
            for (const v of boeking.value.Volwassenen) {
                inschrijvingen.push({
                    Voornaam: v.Voornaam,
                    Achternaam: v.Achternaam,
                    EmailadresVriend: v.Emailadres,
                    LeeftijdVriend: v.Leeftijd,
                    GeslachtVriend: v.Geslacht,
                    VragenAntwoorden: mapVragenAntwoorden(v.Vragen),
                });
            }

            // Kinderen (create/update in account system to get their ID)
            for (const kind of boeking.value.Kinderen) {
                let kindId: number;
                if (kind.Id) {
                    const updated = await profileRepository.updateKind({
                        Id: kind.Id,
                        Voornaam: kind.Voornaam,
                        Achternaam: kind.Achternaam,
                        Leeftijd: kind.Leeftijd!,
                    });
                    kindId = updated?.Id ?? kind.Id;
                } else {
                    const added = await profileRepository.addKind({
                        Voornaam: kind.Voornaam,
                        Achternaam: kind.Achternaam,
                        Leeftijd: kind.Leeftijd!,
                    });
                    kindId = added?.Id ?? 0;
                }
                inschrijvingen.push({
                    Voornaam: kind.Voornaam,
                    Achternaam: kind.Achternaam,
                    Kind: kindId,
                    VragenAntwoorden: mapVragenAntwoorden(kind.Vragen),
                });
            }

            const payload: Record<string, unknown> = {
                Inschrijvingen: inschrijvingen,
                Platform: platformId.value,
                Sportmoment: activiteit.value?.SportmomentId,
                Profiel: profileStore.profile?.Id ?? null,
                AkkoordVoorwaarden: boeking.value.AkkoordVoorwaarden,
                PaymentMethod: boeking.value.PaymentProvider,
                Bijzonderheden: boeking.value.Bijzonderheden || null,
                Actiecode: validActiecode.value?.Id ?? null,
                VragenAntwoorden: mapVragenAntwoorden(boeking.value.Vragen),
            };

            // Read partner cookie set by the platform
            const partnerCookie = document.cookie.split(";").map((c) => c.trim()).find((c) => c.startsWith("partner="))?.split("=")[1] ?? null;
            payload.Partner = partnerCookie;
            payload.Activatie = new URLSearchParams(window.location.search).get("activatie") ?? null;
            payload.Issuer = boeking.value.PaymentProvider;

            if (guestAccount.value) {
                const dob = new Date(account.value.Geboortedatum!);
                dob.setHours(13, 0, 0, 0);
                const clientId = (window as Window & { _clientId?: string })._clientId ?? null;
                payload.GuestModel = {
                    Firstname: account.value.Voornaam,
                    Lastname: account.value.Achternaam,
                    DateOfBirth: dob.toUTCString(),
                    Email: account.value.Emailadres,
                    ZipCode: account.value.Postcode,
                    Phone: account.value.Telefoonnummer,
                    CreateAcccount: upgradeProfile.value,
                    ClientId: clientId,
                };
            }

            const data = await repository.inschrijvenGroep(
                payload,
                activiteit.value.Sportaanbieder,
                guestAccount.value,
            );

            if (data?.StatusCode === 302 && data?.Data?.Url) {
                return { type: "payment", url: data.Data.Url };
            }

            return { type: "success" };
        } catch (err: unknown) {
            const axiosError = err as { response?: { status?: number; data?: { Message?: string; SubStatusCode?: number; Data?: unknown[] } } };
            if (axiosError?.response?.data?.SubStatusCode === 4) {
                maximaalAantalKeerSporten.value = axiosError.response!.data!.Data ?? [];
            }
            const msg = axiosError?.response?.data?.Message || "Er is iets misgegaan bij het afronden van de boeking.";
            submitError.value = msg;
            return { type: "error", message: msg };
        } finally {
            isSubmitting.value = false;
        }
    };

    watch([aantalPersonen, aantalGroepen], async (newValue, oldValue) => {
        if (!isInitialized.value) {
            return;
        }

        if (newValue === oldValue) {
            return;
        }

        try {
            await loadKosten();
        } catch {
            initError.value = "Het laden van de kosten is mislukt.";
        }
    });

    const initialize = async () => {
        if (isInitialized.value || !activiteitId.value || !platformId.value) {
            isInitialized.value = true;
            return;
        }

        isLoading.value = true;
        initError.value = "";
        hasRestoredBoeking.value = false;

        try {
            await platformStore.ensurePlatform();

            const sportmomentId = Number(activiteitId.value);
            activiteit.value = await activiteitRepository.getById(sportmomentId, platformId.value);

            if (!activiteit.value) {
                initError.value = "De activiteit kon niet worden gevonden.";
                return;
            }

            if (activiteit.value?.Type == 2) {
                lessen.value = await activiteitRepository.getSportactiviteitLessen(sportmomentId);
            }

            if (activiteit.value?.Groepsactiviteit && boeking.value.Groepen.length === 0) {
                boeking.value.Groepen = [{ Begeleiders: 0, Deelnemers: 0, OrganisatieNaam: "Groep 1", GroepMaxLeeftijd: 0, GroepMinLeeftijd: 0 }];
            }

            if (activiteit.value?.Sportaanbieder) {
                aanbieder.value = await sportaanbiederRepository.getSportsProviderById(String(activiteit.value.Sportaanbieder));
            }

            if (activiteit.value?.Locatie) {
                locatie.value = await locatieRepository.getLocatie(activiteit.value.Locatie);
            }

            if (activiteit.value?.Sportaanbieder && platformId.value) {
                const allMethods = await repository.getPaymentMethods(
                    activiteit.value.Sportaanbieder,
                    platformId.value,
                );
                const allowedIds = selectedPaymentMethods.value.map((id) => id.toLowerCase());
                paymentMethods.value = allMethods.filter((m) => allowedIds.includes(String(m.Id).toLowerCase()));
            }

            showKortingscodeOptie.value = false;
            validActiecode.value = null;
            invalidActiecode.value = false;
            lockedOut.value = false;
            kortingscode.value = "";

            // Check if discount code option should be shown (BETALING only)
            if (activiteit.value?.Bekostigingstype === BekostigingsType.BETALING) {
                try {
                    showKortingscodeOptie.value = await actiecodeRepository.hasAnyActive(activiteitId.value);
                } catch {
                    showKortingscodeOptie.value = false;
                }
            }

            // Load credits for logged-in users (CREDITS type)
            if (activiteit.value?.Bekostigingstype === BekostigingsType.CREDITS) {
                await loadSpaarpunten();
            }

            vragen.value = await activiteitRepository.getVragen(sportmomentId);
            // Pre-populate groepsvragen in de boeking
            boeking.value.Vragen = buildVraagAntwoorden(
                vragen.value.filter((v) => v.VraagBedoeldVoor === VraagBedoeldVoor.VoorDeGroep),
            );

            // Init guard: paid activity without payment methods is not bookable
            if (
                (activiteit.value?.Bekostigingstype === BekostigingsType.BETALING
                    || activiteit.value?.Bekostigingstype === BekostigingsType.ABONNEMENT)
                && paymentMethods.value.length === 0
            ) {
                initError.value = "Er zijn geen betaalmethoden beschikbaar voor deze activiteit.";
            }

            // Restore boeking from sessionStorage (after login/register/error redirect)
            const savedBoeking = sessionStorage.getItem("bestel_boeking_" + activiteitId.value);
            if (savedBoeking) {
                try {
                    boeking.value = { ...boeking.value, ...JSON.parse(savedBoeking) };
                    hasRestoredBoeking.value = true;
                } catch { /* ignore corrupt data */ }
                sessionStorage.removeItem("bestel_boeking_" + activiteitId.value);
            }

            const savedAccount = sessionStorage.getItem("bestel_account_" + activiteitId.value);
            if (savedAccount) {
                try {
                    account.value = { ...account.value, ...JSON.parse(savedAccount) };
                } catch { /* ignore corrupt data */ }
                sessionStorage.removeItem("bestel_account_" + activiteitId.value);
            }

            const savedGuest = sessionStorage.getItem("bestel_guest_" + activiteitId.value);
            if (savedGuest) {
                try {
                    guestAccount.value = JSON.parse(savedGuest);
                } catch { /* ignore corrupt data */ }
                sessionStorage.removeItem("bestel_guest_" + activiteitId.value);
            }

            if (isAbonnement.value) {
                boeking.value.Jezelf.Include = true;
                boeking.value.Volwassenen = [];
                boeking.value.Kinderen = [];
                boeking.value.Deelnemers = 0;
                boeking.value.Begeleiders = 0;
                boeking.value.Groepen = [{ Begeleiders: 0, Deelnemers: 0, OrganisatieNaam: "", GroepMaxLeeftijd: 0, GroepMinLeeftijd: 0 }];
            }

            await loadKosten();

            isInitialized.value = true;
        } catch {
            initError.value = "Het laden van de activiteit is mislukt.";
        } finally {
            isLoading.value = false;
        }
    };

    const saveBoeking = () => {
        sessionStorage.setItem("bestel_boeking_" + activiteitId.value, JSON.stringify(boeking.value));
        sessionStorage.setItem("bestel_account_" + activiteitId.value, JSON.stringify(account.value));
        sessionStorage.setItem("bestel_guest_" + activiteitId.value, JSON.stringify(guestAccount.value));
    };

    return {
        aantalGroepen,
        aantalPersonen,
        aantalPlaatsenBeschikbaar,
        account,
        addGroup,
        alleKosten,
        aanbieder,
        abonnementPeriode,
        activiteit,
        activiteitId,
        addKind,
        addVolwassene,
        algemeneVoorwaardenLabel,
        algemeneVoorwaardenText,
        boeking,
        checkGolfstart,
        continueAsGuest,
        groepsVragen,
        guestAccount,
        hasRestoredBoeking,
        hasPaymentMethods,
        individueleVragen,
        invalidActiecode,
        isAbonnement,
        isGeweest,
        isGolfstartAllowed,
        isFriendsAllowed,
        isInschrijvingPeriodeActief,
        isLoadingKosten,
        isProfileAllowed,
        isSubmitting,
        isValidatingActiecode,
        kortingscode,
        lockedOut,
        maximaalAantalKeerSporten,
        notEnoughCredits,
        paymentMethods,
        remainingSpaarpunten,
        removeGroup,
        resetActiecode,
        saveBoeking,
        showKortingscodeOptie,
        showPaymentMethod,
        sidebarItems,
        spaarpuntenCount,
        submitBoeking,
        submitError,
        terugUrl,
        upgradeProfile,
        validateActiecode,
        validActiecode,
        vragen,
        wieGaanErContentblockTitle,
        initError,
        initialize,
        isInitialized,
        isLoading,
        lessen,
        loadKosten,
        locatie,
        platform,
        platformId,
        removeKind,
        removeVolwassene,
        selectedPaymentMethods,
        setBegeleiders,
        setDeelnemers,
        setJezelfInclude,
    };
});
